import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { roleLabel } from "@/lib/constants";
import { UserAvatar } from "@/components/UserAvatar";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Ban, EyeOff, KeyRound, Lock, LogIn, LogOut, ShieldAlert, Unlock } from "lucide-react";
import { format, formatDistanceToNow, parseISO } from "date-fns";
import { pt } from "date-fns/locale";

const EVENTS = {
  LOGIN: { label: "Entrou", icon: LogIn, className: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" },
  LOGOUT: { label: "Saiu", icon: LogOut, className: "bg-muted text-muted-foreground" },
  LOGIN_FAILED: { label: "Falhou", icon: ShieldAlert, className: "bg-amber-500/15 text-amber-700 dark:text-amber-400" },
  LOGIN_BLOCKED: { label: "Bloqueado", icon: Ban, className: "bg-destructive/15 text-destructive" },
  LOGS_OPENED: { label: "Abriu os logs", icon: Unlock, className: "bg-muted text-muted-foreground" },
  LOGS_UNLOCK_FAILED: { label: "Password dos logs errada", icon: KeyRound, className: "bg-destructive/15 text-destructive" },
};

const ago = (iso) => (iso ? formatDistanceToNow(parseISO(iso), { addSuffix: true, locale: pt }) : "nunca");
const when = (iso) => format(parseISO(iso), "d MMM, HH:mm", { locale: pt });

function UnlockForm() {
  const qc = useQueryClient();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/access-logs/unlock", { password });
      setPassword("");
      qc.invalidateQueries({ queryKey: ["access-logs", "data"] });
    } catch (err) {
      setError(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mx-auto max-w-sm p-6 border-border" data-testid="logs-unlock">
      <form onSubmit={submit} className="space-y-3">
        <div className="flex items-center gap-2 font-poppins font-semibold text-sm">
          <Lock className="h-4 w-4 text-muted-foreground" /> Logs
        </div>
        <Input
          type="password"
          autoComplete="off"
          autoFocus
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="logs-password"
        />
        {error && <p className="text-xs text-destructive">{error}</p>}
        <Button type="submit" disabled={busy || !password} className="w-full bg-primary text-black hover:bg-primary/90" data-testid="logs-unlock-btn">
          {busy ? "A verificar..." : "Abrir"}
        </Button>
      </form>
    </Card>
  );
}

/** Logs de acessos — só aparecem a quem o servidor autoriza (ACCESS_LOG_VIEWERS)
 *  e abrem com password; ficam abertos 15 minutos. */
export function AccessLogPanel() {
  const [personId, setPersonId] = useState("all");
  const { data, error } = useQuery({
    queryKey: ["access-logs", "data", personId],
    queryFn: async () => (await api.get("/access-logs", { params: personId === "all" ? {} : { userId: personId } })).data,
    refetchInterval: 60_000,
    retry: false,
  });

  // 423 = bloqueados (ainda não abertos, ou passaram os 15 minutos).
  if (error?.response?.status === 423) return <UnlockForm />;
  if (error) return <p className="text-sm text-destructive">{apiError(error.response?.data?.detail)}</p>;
  if (!data) return <p className="text-sm text-muted-foreground">A carregar...</p>;
  const { people, events, retentionDays } = data;
  const nameById = Object.fromEntries(people.map((p) => [p.id, p.name]));

  return (
    <div className="space-y-6" data-testid="access-log">
      <p className="text-xs text-muted-foreground flex items-center gap-1.5">
        <EyeOff className="h-3.5 w-3.5 shrink-0" />
        Visível só para as contas autorizadas no servidor. Os registos apagam-se ao fim de {retentionDays} dias.
      </p>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {people.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setPersonId(personId === p.id ? "all" : p.id)}
            className={`rounded-xl border bg-card p-4 text-left transition-colors hover:border-primary/40 ${personId === p.id ? "border-primary ring-1 ring-primary" : "border-border"}`}
            data-testid={`access-person-${p.id}`}
          >
            <div className="flex items-center gap-3">
              <UserAvatar user={p} />
              <div className="min-w-0">
                <div className="font-poppins font-medium truncate">
                  {p.name}
                  {p.status === "INATIVO" && <span className="ml-1 text-xs font-normal text-muted-foreground">· inativo</span>}
                </div>
                <div className="text-xs text-muted-foreground truncate">{roleLabel(p)}</div>
              </div>
            </div>
            <dl className="mt-3 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
              <dt className="text-muted-foreground">Última atividade</dt>
              <dd className="font-medium">{ago(p.lastSeenAt)}</dd>
              <dt className="text-muted-foreground">Último login</dt>
              <dd>{p.lastLogin ? when(p.lastLogin.at) : "—"}</dd>
              <dt className="text-muted-foreground">Dispositivo</dt>
              <dd className="truncate">{p.lastLogin?.device || "—"}</dd>
              <dt className="text-muted-foreground">Logins (30 dias)</dt>
              <dd>{p.logins30d}</dd>
            </dl>
            {p.failed7d > 0 && (
              <Badge className="mt-3 rounded-full font-normal bg-amber-500/15 text-amber-700 dark:text-amber-400">
                {p.failed7d} {p.failed7d === 1 ? "tentativa falhada" : "tentativas falhadas"} nos últimos 7 dias
              </Badge>
            )}
          </button>
        ))}
      </div>

      <Card className="p-4 border-border">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h3 className="font-poppins font-semibold text-sm">Histórico</h3>
          <Select value={personId} onValueChange={setPersonId}>
            <SelectTrigger className="h-8 w-48 text-xs" data-testid="access-person-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as pessoas</SelectItem>
              {people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {events.length === 0 ? (
          <p className="text-sm text-muted-foreground">Sem registos.</p>
        ) : (
          <div className="divide-y divide-border/60">
            {events.map((e) => {
              const meta = EVENTS[e.event] || EVENTS.LOGIN;
              const Icon = meta.icon;
              return (
                <div key={e.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm">
                  <span className="w-24 shrink-0 text-xs text-muted-foreground tabular-nums">{when(e.timestamp)}</span>
                  <Badge className={`rounded-full gap-1 font-normal ${meta.className}`}>
                    <Icon className="h-3 w-3" /> {meta.label}
                  </Badge>
                  <span className="font-medium">{e.userName || nameById[e.userId] || e.identifier || "—"}</span>
                  {e.detail && <span className="text-xs text-muted-foreground">({e.detail})</span>}
                  {e.remember && <span className="text-xs text-muted-foreground">· manter sessão</span>}
                  <span className="w-full text-xs text-muted-foreground sm:ml-auto sm:w-auto">{e.device} · {e.ip}</span>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
